import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Form, InputGroup, Badge, Table, Modal, Spinner, Navbar, Nav } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ManagerOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState('');
  const [ordersError, setOrdersError] = useState('');
  const [profile, setProfile] = useState(null);
  const [menus, setMenus] = useState([]);
  const [selectedOrdersMenuId, setSelectedOrdersMenuId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      fetchMenus();
      fetchOrders();
    }
  }, [profile]);

  // WebSocket handles real-time updates, no need for manual refresh on menu change

  // Real-time order updates using WebSocket
  useEffect(() => {
    if (!profile) return;
    
    let fallbackInterval = null;
    let ws = null;
    let isConnecting = false;
    let isMounted = true;
    
      const connectWebSocket = () => {
        if (!isMounted || isConnecting || (ws && ws.readyState === WebSocket.OPEN)) {
          return; // Already connecting or connected, or component unmounted
        }
        
        isConnecting = true;
      const restaurantId = profile.restaurantId || profile._id;
      const wsUrl = `wss://menu.qr-code.by/ws/orders/${restaurantId}`;
        
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          if (!isMounted) {
            ws.close();
            return;
          }
          isConnecting = false;
          // Clear any existing fallback interval
          if (fallbackInterval) {
            clearInterval(fallbackInterval);
            fallbackInterval = null;
          }
        };
        
        ws.onmessage = (event) => {
          if (!isMounted) return;
          
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'new_order' || data.type === 'order_updated') {
              // Refresh orders when new order arrives
              fetchOrders();
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        ws.onerror = (error) => {
          if (!isMounted) return;
          
          isConnecting = false;
          // Start fallback polling if not already running
          if (!fallbackInterval) {
            fallbackInterval = setInterval(() => {
              if (isMounted) {
                fetchOrders();
              }
            }, 10000); // Poll every 10 seconds as fallback
          }
        };
        
        ws.onclose = (event) => {
          if (!isMounted) return;
          
          isConnecting = false;
          
          // Only start fallback if it's not a normal closure and component is still mounted
          if (event.code !== 1000 && !fallbackInterval && isMounted) {
            fallbackInterval = setInterval(() => {
              if (isMounted) {
                fetchOrders();
              }
            }, 10000); // Poll every 10 seconds as fallback
          }
        };
      };
    
    // Connect immediately
    connectWebSocket();

    return () => {
      isMounted = false;
      if (ws) {
        ws.close(1000, 'Component unmounting');
      }
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
      }
    };
  }, [profile?.restaurantId, profile?._id]); // Only depend on the actual IDs, not the whole profile object

  useEffect(() => {
    let filtered = orders;
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    
    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        switch (dateFilter) {
          case 'today':
            return orderDate >= today;
          case 'yesterday':
            return orderDate >= yesterday && orderDate < today;
          case 'week':
            return orderDate >= weekAgo;
          default:
            return true;
        }
      });
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.tableNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [orders, statusFilter, dateFilter, searchTerm]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('managerToken');
      if (!token) {
        setError('Токен не найден');
        setLoading(false);
        return;
      }

      const res = await axios.get('/api/manager/profile', {
        headers: { 'x-auth-token': token }
      });
      setProfile(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.message || 'Ошибка загрузки профиля');
      setLoading(false);
    }
  };

  const fetchMenus = async () => {
    try {
      const token = localStorage.getItem('managerToken');
      
      // Get manager profile first to get restaurantId
      const profileRes = await axios.get('/api/manager/profile', {
        headers: { 'x-auth-token': token }
      });
      
      const restaurantId = profileRes.data.restaurantId;
      
      const res = await axios.get(`/api/menu/${restaurantId}/list`, {
        headers: { 'x-auth-token': token }
      });
      setMenus(res.data);
    } catch (err) {
      console.error('Error fetching menus:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      setOrdersError('');

      const token = localStorage.getItem('managerToken');
      const params = new URLSearchParams();
      
      if (selectedOrdersMenuId) {
        params.set('menuId', selectedOrdersMenuId);
      }

      const res = await axios.get(`/api/manager/orders?${params.toString()}`, {
        headers: { 'x-auth-token': token }
      });
      
      // Only update if data has changed
      const newOrders = res.data;
      setOrders(prevOrders => {
        // Compare if orders have actually changed
        if (JSON.stringify(prevOrders) === JSON.stringify(newOrders)) {
          return prevOrders; // No change, keep previous reference
        }
        return newOrders;
      });
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrdersError(err.response?.data?.message || 'Ошибка загрузки заказов');
    } finally {
      setLoadingOrders(false);
    }
  };

  const getMenuLabel = (id) => {
    const m = menus.find((x) => x._id === id);
    if (!m) return '—';
    return `${m.title} — ${m.mode === 'dine_in' ? 'В зале' : 'Доставка'}`;
  };

  // Order status badge helper
  const getOrderStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Ожидает</Badge>;
      case 'accepted':
        return <Badge bg="info">Принят</Badge>;
      case 'completed':
        return <Badge bg="success">Завершен</Badge>;
      case 'rejected':
        return <Badge bg="danger">Отклонен</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Payment status badge helper
  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Ожидает оплаты</Badge>;
      case 'paid':
        return <Badge bg="success">Оплачен</Badge>;
      case 'completed':
        return <Badge bg="success">Оплачен</Badge>;
      case 'failed':
        return <Badge bg="danger">Ошибка оплаты</Badge>;
      case 'refunded':
        return <Badge bg="info">Возврат</Badge>;
      default:
        return <Badge bg="secondary">{status || 'Не указано'}</Badge>;
    }
  };

  // Status icon helper
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'accepted':
        return '✅';
      case 'completed':
        return '🎉';
      case 'rejected':
        return '❌';
      default:
        return '❓';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return `${price?.toFixed(2) || '0.00'} BYN`;
  };

  // Payment provider text helper
  const getPaymentProviderText = (provider) => {
    const providerMap = {
      'expresspay': 'ExpressPay',
      'cash': 'Наличные',
      'card': 'Карта',
      'onsite': 'На месте'
    };
    return providerMap[provider] || provider || '—';
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('managerToken');
      
      // Find the order to check payment provider
      const order = orders.find(o => o._id === orderId);
      const isExpressPay = order?.paymentProvider === 'expresspay';
      
      // Prepare request body
      const requestBody = { status };
      
      // If completing order and not ExpressPay, also update payment status
      if (status === 'completed' && !isExpressPay) {
        requestBody.paymentStatus = 'completed';
      }
      
      const response = await axios.put(
        `/api/manager/orders/${orderId}/status`,
        requestBody,
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Update the order in the local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order._id === orderId 
              ? { 
                  ...order, 
                  status: status, 
                  paymentStatus: status === 'completed' && !isExpressPay ? 'completed' : order.paymentStatus,
                  processedBy: response.data.order?.processedBy 
                }
              : order
          )
        );
        
        // Update selected order if it's the same
        if (selectedOrder && selectedOrder._id === orderId) {
          setSelectedOrder(prev => {
            if (!prev) return prev; // Guard against null
            return { 
              ...prev, 
              status: status, 
              paymentStatus: status === 'completed' && !isExpressPay ? 'completed' : prev.paymentStatus,
              processedBy: response.data.order?.processedBy 
            };
          });
        }
        
        // WebSocket will handle real-time updates, no need for manual refresh
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Ошибка при обновлении статуса заказа');
    }
  };

  const updatePaymentStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('managerToken');
      await axios.put(`/api/orders/${orderId}/payment-status`, 
        { paymentStatus: newStatus },
        { headers: { 'x-auth-token': token } }
      );
      
      // Update local state
      setOrders(orders.map(order => 
        order._id === orderId ? { ...order, paymentStatus: newStatus } : order
      ));
      
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder(prev => {
          if (!prev) return prev; // Guard against null
          return { ...prev, paymentStatus: newStatus };
        });
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('managerToken');
    navigate('/');
  };

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </Spinner>
        <p className="mt-2">Загрузка профиля...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Ошибка</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (ordersError) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Ошибка загрузки заказов</Alert.Heading>
          <p>{ordersError}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Simple Navigation */}
      <Navbar className="modern-navbar" expand="lg" sticky="top">
        <Container>
          <Navbar.Brand className="navbar-brand-custom">
            <i className="bi bi-qr-code me-2"></i>
            <span className="brand-text">QR Menu</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" className="navbar-toggle-custom" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto nav-actions-custom">
              <Button 
                variant="outline-light" 
                onClick={handleLogout}
                className="logout-btn-custom"
              >
                <i className="bi bi-box-arrow-right me-1"></i>
                Выйти
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h1>Управление заказами</h1>
          <p>Просматривайте и управляйте заказами клиентов</p>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="sm"
          >
            <option value="all">Все статусы</option>
            <option value="pending">Ожидает</option>
            <option value="accepted">Принят</option>
            <option value="completed">Завершен</option>
            <option value="rejected">Отклонен</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            size="sm"
          >
            <option value="all">Все даты</option>
            <option value="today">Сегодня</option>
            <option value="yesterday">Вчера</option>
            <option value="week">За неделю</option>
          </Form.Select>
        </Col>
        <Col md={4}>
          <InputGroup size="sm">
            <InputGroup.Text>Поиск</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="По номеру заказа, столу или клиенту..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <div className="d-flex gap-1">
            <Button
              variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('cards')}
            >
              📋
            </Button>
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              📊
            </Button>
          </div>
        </Col>
      </Row>

      {/* Orders Display */}
      {loadingOrders ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Загрузка заказов...</span>
          </Spinner>
          <p className="mt-2">Загрузка заказов...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <Alert variant="info">
          <Alert.Heading>Заказы не найдены</Alert.Heading>
          <p>
            {searchTerm || statusFilter !== 'all' || dateFilter !== 'all' || selectedOrdersMenuId
              ? 'Попробуйте изменить фильтры поиска'
              : 'Заказы появятся здесь, когда клиенты сделают заказ'}
          </p>
        </Alert>
      ) : viewMode === 'cards' ? (
        <Row>
          {currentOrders.map((order) => (
            <Col key={order._id} md={6} lg={4} className="mb-3">
              <Card 
                className="h-100"
                style={{ cursor: 'pointer' }}
                onClick={() => handleOrderClick(order)}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">{order.orderNumber || `#${order._id.slice(-6)}`}</h6>
                    <div className="d-flex gap-1">
                      {getOrderStatusBadge(order.status)}
                      {getPaymentStatusBadge(order.paymentStatus)}
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted">
                      {getStatusIcon(order.status)} {formatDate(order.createdAt)}
                    </small>
                  </div>
                  
                  <div className="mb-2">
                    <strong>Меню:</strong> {getMenuLabel(order.menuId)}
                  </div>
                  <div className="mb-2">
                    <strong>Стол:</strong> {order.tableNumber || '—'}
                  </div>
                  
                  <div className="mb-2">
                    <strong>Клиент:</strong> {order.customerName || '—'}
                  </div>
                  
                  <div className="mb-2">
                    <strong>Сумма:</strong> {formatPrice(order.totalAmount)}
                  </div>
                  
                  <div className="mb-2">
                    <strong>Тип оплаты:</strong> {getPaymentProviderText(order.paymentProvider)}
                  </div>
                  
                  {/* Кнопки управления заказом */}
                  {order.status === 'pending' && (
                    <div className="d-flex gap-2">
                      <Button 
                        variant="success" 
                        size="sm" 
                        className="flex-fill"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order._id, 'accepted');
                        }}
                      >
                        ✅ Принять
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm" 
                        className="flex-fill"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order._id, 'rejected');
                        }}
                      >
                        ❌ Отклонить
                      </Button>
                    </div>
                  )}
                  
                  {order.status === 'accepted' && (
                    <div className="d-flex gap-2">
                      <Button 
                        variant="primary" 
                        size="sm" 
                        className="flex-fill"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order._id, 'completed');
                        }}
                      >
                        ✅ Завершить
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Table striped hover responsive>
          <thead>
            <tr>
              <th>№</th>
              <th>Статус</th>
              <th>Оплата</th>
              <th>Тип оплаты</th>
              <th>Стол</th>
              <th>Клиент</th>
              <th>Меню</th>
              <th>Сумма</th>
              <th>Дата</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.map((order) => (
              <tr key={order._id}>
                <td>{order.orderNumber || `#${order._id.slice(-6)}`}</td>
                <td>{getOrderStatusBadge(order.status)}</td>
                <td>{getPaymentStatusBadge(order.paymentStatus)}</td>
                <td>{getPaymentProviderText(order.paymentProvider)}</td>
                <td>{order.tableNumber || '—'}</td>
                <td>
                  <div>{order.customerName || '—'}</div>
                  <small className="text-muted">{order.customerPhone || '—'}</small>
                </td>
                <td>{getMenuLabel(order.menuId)}</td>
                <td>{formatPrice(order.totalAmount)}</td>
                <td>
                  <div>{formatDate(order.createdAt)}</div>
                </td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleOrderClick(order)}
                    >
                      Подробнее
                    </Button>
                    
                    {order.status === 'pending' && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => updateOrderStatus(order._id, 'accepted')}
                        >
                          ✅ Принять
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => updateOrderStatus(order._id, 'rejected')}
                        >
                          ❌ Отклонить
                        </Button>
                      </>
                    )}
                    
                    {order.status === 'accepted' && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => updateOrderStatus(order._id, 'completed')}
                      >
                        ✅ Завершить
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Row className="mt-4">
          <Col>
            <div className="d-flex justify-content-center">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Предыдущая
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => paginate(number)}
                      >
                        {number}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Следующая
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </Col>
        </Row>
      )}

      {/* Order Details Modal */}
      <Modal show={showOrderModal && selectedOrder} onHide={handleCloseOrderModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Заказ {selectedOrder?.orderNumber || `#${selectedOrder?._id.slice(-6)}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Статус заказа:</strong>
                  <div className="mt-1">
                    {getOrderStatusBadge(selectedOrder.status)}
                    <div className="mt-2">
                      <Form.Select
                        size="sm"
                        value={selectedOrder.status}
                        onChange={(e) => updateOrderStatus(selectedOrder._id, e.target.value)}
                      >
                        <option value="pending">Ожидает</option>
                        <option value="accepted">Принят</option>
                        <option value="completed">Завершен</option>
                        <option value="rejected">Отклонен</option>
                      </Form.Select>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <strong>Статус оплаты:</strong>
                  <div className="mt-1">
                    {getPaymentStatusBadge(selectedOrder.paymentStatus)}
                    <div className="mt-2">
                      <Form.Select
                        size="sm"
                        value={selectedOrder.paymentStatus || 'pending'}
                        onChange={(e) => updatePaymentStatus(selectedOrder._id, e.target.value)}
                      >
                        <option value="pending">Ожидает оплаты</option>
                        <option value="paid">Оплачен</option>
                        <option value="completed">Оплачен</option>
                        <option value="failed">Ошибка оплаты</option>
                        <option value="refunded">Возврат</option>
                      </Form.Select>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Дата заказа:</strong> {formatDate(selectedOrder.createdAt)}</p>
                  <p><strong>Тип заказа:</strong> {selectedOrder.orderType === 'dine_in' ? 'В зале' : 'Доставка'}</p>
                  <p><strong>Тип оплаты:</strong> {getPaymentProviderText(selectedOrder.paymentProvider)}</p>
                  <p><strong>Стол:</strong> {selectedOrder.tableNumber || '—'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Клиент:</strong> {selectedOrder.customerName || '—'}</p>
                  <p><strong>Телефон:</strong> {selectedOrder.customerPhone || '—'}</p>
                  <p><strong>Адрес:</strong> {selectedOrder.deliveryAddress || '—'}</p>
                </Col>
              </Row>

              <div className="mb-3">
                <strong>Меню:</strong> {getMenuLabel(selectedOrder.menuId)}
              </div>

              <div className="mb-3">
                <strong>Заказанные блюда:</strong>
                <Table striped size="sm" className="mt-2">
                  <thead>
                    <tr>
                      <th>Блюдо</th>
                      <th>Количество</th>
                      <th>Цена</th>
                      <th>Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <div>{item.name}</div>
                          {item.addons?.length > 0 && (
                            <small className="text-muted">
                              Добавки: {item.addons.map(addon => addon.name).join(', ')}
                            </small>
                          )}
                          {item.addonGroups?.length > 0 && (
                            <small className="text-muted">
                              Группы: {item.addonGroups.map(group => 
                                `${group.name}: ${group.options?.join(', ')}`
                              ).join(', ')}
                            </small>
                          )}
                        </td>
                        <td>{item.quantity}</td>
                        <td>{formatPrice(item.price)}</td>
                        <td>{formatPrice(item.price * item.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="mb-3">
                <strong>Общая сумма:</strong> {formatPrice(selectedOrder.totalAmount)}
              </div>

              {selectedOrder.specialInstructions && (
                <div className="mb-3">
                  <strong>Особые указания:</strong>
                  <p className="mt-1">{selectedOrder.specialInstructions}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex gap-2 w-100">
            <Button variant="secondary" onClick={handleCloseOrderModal}>
              Закрыть
            </Button>
            
            {selectedOrder?.status === 'pending' && (
              <>
                <Button 
                  variant="success" 
                  onClick={() => {
                    updateOrderStatus(selectedOrder._id, 'accepted');
                    handleCloseOrderModal();
                  }}
                >
                  ✅ Принять заказ
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => {
                    updateOrderStatus(selectedOrder._id, 'rejected');
                    handleCloseOrderModal();
                  }}
                >
                  ❌ Отклонить заказ
                </Button>
              </>
            )}
            
            {selectedOrder?.status === 'accepted' && (
              <Button 
                variant="primary" 
                onClick={() => {
                  updateOrderStatus(selectedOrder._id, 'completed');
                  handleCloseOrderModal();
                }}
              >
                ✅ Завершить заказ
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default ManagerOrdersPage;
